import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeBriefHeroLook = 'briefhero-threat-red' | 'briefhero-threat-amber' | 'briefhero-threat-accent' | 'briefhero-threat-none';

export const isWedgeBriefHeroLook = (look: string | undefined): look is WedgeBriefHeroLook => look !== undefined && look.startsWith('briefhero-');

/* Released brief hero (Wedge, the main brief header): the threat row under the account name, a full width tappable banner that opens the
   status explainer. It is a 44px floor row, 10px by 14px padding and 12px radius, a flex row with a 10px gap, in 13.5px at 600 weight, with a
   half pixel edge. The row takes the hue of the threat tier: Priority is red, Warm amber, Watching accent, and an unrated account the tertiary
   label ink on the elevated surface (the legacy soft role of that ink never existed, so its fill was always the elevated surface). The fill is
   the soft wash of the hue, the ink and the edge the hue itself. Each look keeps the declaration order of the source so the shipped ink rules
   keep matching the serialized text. No look draws a transparent target and no anchor: the row is already 44px tall and sits in a padded block.
   The caller owns the tier dot, the label and the glyph. */
const TONES = {
  'briefhero-threat-red': ['red', 'redSoft'],
  'briefhero-threat-amber': ['amber', 'amberSoft'],
  'briefhero-threat-accent': ['accent', 'accentSoft'],
  'briefhero-threat-none': ['labelTer', 'bgElev'],
} as const;

export function wedgeBriefHeroStyle(look: WedgeBriefHeroLook): CSSProperties {
  const r = wedgeExactRole;
  const [ink, fill] = TONES[look];
  return {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
    padding: '10px 14px', minHeight: 44, boxSizing: 'border-box',
    background: r(fill), color: r(ink), borderRadius: 12,
    fontSize: 13.5, fontWeight: 600,
    border: `0.5px solid ${r(ink)}`, cursor: 'pointer',
    fontFamily: r('font'), textAlign: 'left',
  };
}
